// src/components/report/PartC.jsx - Fixed with 2 domains per page for better formatting
import React from 'react';

// Proficiency Diagram Component matching the reference
const ProficiencyDiagram = () => (
    <div className="w-32 h-32 mx-auto mb-4">
        <div className="w-full h-full rounded-full overflow-hidden border-2 border-gray-400 relative"
             style={{
                 background: 'linear-gradient(to bottom, #87CEEB 0%, #87CEEB 30%, #90EE90 30%, #90EE90 60%, #4682B4 60%, #4682B4 100%)'
             }}>
            {/* Sky section */}
            <div className="absolute top-2 left-1/2 transform -translate-x-1/2 font-bold text-blue-800 text-xs">
                Sky
            </div>

            {/* Mountain section */}
            <div
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 font-bold text-green-800 text-xs">
                Mountain
            </div>

            {/* Stream section */}
            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 font-bold text-blue-800 text-xs">
                Stream
            </div>

            {/* Levels of Proficiency vertical text */}
            <div
                className="absolute left-1 top-1/2 transform -translate-y-1/2 -rotate-90 font-bold text-blue-800 text-xs whitespace-nowrap">
                Levels of Proficiency
            </div>

            {/* Vertical arrow */}
            <div className="absolute left-3 top-4 bottom-4 w-0.5 bg-blue-800"></div>
            <div
                className="absolute left-3 top-4 w-0 h-0 border-l-2 border-r-2 border-b-3 border-l-transparent border-r-transparent border-b-blue-800 transform rotate-180"></div>
        </div>
    </div>
);

// Proficiency Section Component with red first letter
const ProficiencySection = ({title}) => {
    const firstLetter = title.charAt(0);
    const restOfTitle = title.slice(1);

    return (
        <div className="text-center mb-8">
            <div className="mb-3">
                <span className="text-6xl font-bold text-red-700" style={{fontFamily: 'Times New Roman, serif'}}>
                    {firstLetter}
                </span>
                <span className="text-xl font-normal" style={{fontFamily: 'Times New Roman, serif'}}>
                    {restOfTitle}
                </span>
            </div>
            <ProficiencyDiagram/>
        </div>
    );
};

// Content text component that wraps text properly
const ContentText = ({text}) => {
    if (!text) return (
        <div className="space-y-2 mb-4">
            {Array(3).fill(0).map((_, index) => (
                <div key={index} className="border-b border-gray-600 h-4"></div>
            ))}
        </div>
    );

    return (
        <div className="mb-4">
            <div className="text-xs leading-relaxed p-2 bg-white bg-opacity-70 rounded">
                {text}
            </div>
        </div>
    );
};

// Single page component for Part C
const PartCPage = ({domains, isFirstPage, pageNumber}) => (
    <div className="report-page relative bg-white" style={{
        width: '210mm',
        height: '297mm',
        minHeight: '297mm',
        padding: '15mm 20mm',
        margin: '0 auto',
        fontSize: '10pt',
        lineHeight: '1.4',
        fontFamily: 'Arial, Helvetica, sans-serif',
        color: '#333333',
        boxSizing: 'border-box',
        overflow: 'hidden'
    }}>
        {/* Orange corner triangles */}
        <div className="absolute top-0 right-0 w-0 h-0"
             style={{
                 borderTop: '60px solid #F97316',
                 borderLeft: '60px solid transparent'
             }}>
        </div>
        <div className="absolute bottom-0 left-0 w-0 h-0"
             style={{
                 borderBottom: '60px solid #F97316',
                 borderRight: '60px solid transparent'
             }}>
        </div>

        {/* Header Section - only on first page */}
        {isFirstPage && (
            <div className="text-center mb-6">
                <h1 className="text-2xl font-bold text-orange-600 mb-2">PART C</h1>
                <h2 className="text-xl font-bold text-orange-600 mb-2">SUMMARY FOR THE ACADEMIC YEAR</h2>
                <h3 className="text-lg font-bold mb-2">KEY PERFORMANCE DESCRIPTORS</h3>
                <p className="text-sm italic">(Qualitative inputs by teacher based on the student's ability)</p>
            </div>
        )}

        {/* Main Content Layout */}
        <div className="flex border-2 border-gray-600 h-auto" style={{minHeight: isFirstPage ? '500px' : '580px'}}>
            {/* Left Column - Proficiency Sections */}
            <div className="w-1/3 border-r-2 border-gray-600 p-4 bg-white">
                <ProficiencySection title="Awareness"/>
                <ProficiencySection title="Sensitivity"/>
                <ProficiencySection title="Creativity"/>
            </div>

            {/* Right Column - Domain Content */}
            <div className="w-2/3 relative bg-orange-100 p-4" style={{backgroundColor: '#FFF8DC'}}>
                {/* Orange decorative corner */}
                {/*<div*/}
                {/*    className="absolute bottom-0 right-0 w-20 h-20 bg-orange-500 transform rotate-45 translate-x-10 translate-y-10"></div>*/}

                {/* Domain Sections with actual API data */}
                <div className="relative z-10">
                    {domains.map((domain, index) => (
                        <div key={domain.domain_id} className="mb-6">
                            <h4 className="font-bold text-sm mb-3">
                                {domain.displayIndex}. {domain.domain_name}
                            </h4>

                            {/* Awareness Content */}
                            <div className="mb-4">
                                <ContentText text={domain.awareness}/>
                            </div>

                            {/* Sensitivity Content */}
                            <div className="mb-4">
                                <ContentText text={domain.sensitivity}/>
                            </div>

                            {/* Creativity Content */}
                            <div className="mb-4">
                                <ContentText text={domain.creativity}/>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>

        {/* Footer Note - only on first page */}
        {isFirstPage && (
            <div className="mt-4">
                <p className="text-xs">
                    <span className="font-bold">NOTE:</span> A summary of the holistic development of the student needs
                    to be given at the end of an academic year in a descriptive manner in each of the five domains.
                    Essentially each of the summary should emphasize the strengths as well as the area of concerns/
                    improvements. The performances summaries should be described in terms of 3 abilities (i.e.,
                    Awareness, Sensitivity, Creativity).
                </p>
            </div>
        )}

        {/* Page number */}
        {pageNumber && (
            <div className="absolute bottom-4 left-0 right-0 text-center text-sm font-medium">
                {pageNumber}
            </div>
        )}
    </div>
);

// Special page for domain 5 and positive learning habits
const PartCFinalPage = ({domain, pageNumber}) => (
    <div className="report-page relative bg-white" style={{
        width: '210mm',
        height: '297mm',
        minHeight: '297mm',
        padding: '15mm 20mm',
        margin: '0 auto',
        fontSize: '10pt',
        lineHeight: '1.4',
        fontFamily: 'Arial, Helvetica, sans-serif',
        color: '#333333',
        boxSizing: 'border-box',
        overflow: 'hidden'
    }}>
        {/* Orange corner triangles */}
        <div className="absolute top-0 right-0 w-0 h-0"
             style={{
                 borderTop: '60px solid #F97316',
                 borderLeft: '60px solid transparent'
             }}>
        </div>
        <div className="absolute bottom-0 left-0 w-0 h-0"
             style={{
                 borderBottom: '60px solid #F97316',
                 borderRight: '60px solid transparent'
             }}>
        </div>

        {/* Main Content Layout */}
        <div className="flex border-2 border-gray-600 h-auto min-h-[580px] mt-4">
            {/* Left Column - Proficiency Sections */}
            <div className="w-1/3 border-r-2 border-gray-600 p-4 bg-white">
                <ProficiencySection title="Awareness"/>
                <ProficiencySection title="Sensitivity"/>
                <ProficiencySection title="Creativity"/>
            </div>

            {/* Right Column - Domain Content */}
            <div className="w-2/3 relative bg-orange-100 p-4" style={{backgroundColor: '#FFF8DC'}}>
                {/* Orange decorative corner */}
                {/*<div*/}
                {/*    className="absolute bottom-0 right-0 w-20 h-20 bg-orange-500 transform rotate-45 translate-x-10 translate-y-10"></div>*/}

                {/* Domain 5 Content */}
                <div className="relative z-10">
                    <div className="mb-6">
                        <h4 className="font-bold text-sm mb-3">5. {domain.domain_name}</h4>

                        {/* Awareness Content */}
                        <div className="mb-4">
                            <ContentText text={domain.awareness}/>
                        </div>

                        {/* Sensitivity Content */}
                        <div className="mb-4">
                            <ContentText text={domain.sensitivity}/>
                        </div>

                        {/* Creativity Content */}
                        <div className="mb-4">
                            <ContentText text={domain.creativity}/>
                        </div>
                    </div>

                    {/* 5.1. Positive Learning Habits section */}
                    <div className="mb-6">
                        <h4 className="font-bold text-sm mb-3">5.1. Positive Learning Habits</h4>
                        <div className="space-y-2">
                            {Array(9).fill(0).map((_, index) => (
                                <div key={index} className="border-b border-gray-600 h-4"></div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* Page number */}
        {pageNumber && (
            <div className="absolute bottom-4 left-0 right-0 text-center text-sm font-medium">
                {pageNumber}
            </div>
        )}
    </div>
);

const PartC = ({data, pageNumber}) => {
    console.log('PART C DATA: ', data);

    if (!data || data.length === 0) return <div className="report-page">Loading Part C...</div>;

    // Map API data to expected domain order
    const domainMapping = {
        "Physical Development": 1,
        "Socio-Emotional and Ethical Development": 2,
        "Cognitive Development": 3,
        "Language and Literacy Development": 4,
        "Aesthetic and Cultural Development": 5
    };

    // Sort domains by the expected order and add display index
    const sortedData = [...data].sort((a, b) => {
        const orderA = domainMapping[a.domain_name] || 999;
        const orderB = domainMapping[b.domain_name] || 999;
        return orderA - orderB;
    }).map(domain => ({
        ...domain,
        displayIndex: domainMapping[domain.domain_name]
    }));

    // Create pages with 2 domains each
    const pages = [];

    // Page 1: Domains 1-2
    if (sortedData.length >= 2) {
        pages.push({
            domains: sortedData.slice(0, 2),
            isFirstPage: true,
            pageNumber: pageNumber || 17
        });
    }

    // Page 2: Domains 3-4
    if (sortedData.length >= 4) {
        pages.push({
            domains: sortedData.slice(2, 4),
            isFirstPage: false,
            pageNumber: (pageNumber || 17) + 1
        });
    }

    // Page 3: Domain 5 + Positive Learning Habits
    if (sortedData.length >= 5) {
        pages.push({
            domain: sortedData[4],
            isSpecialPage: true,
            pageNumber: (pageNumber || 17) + 2
        });
    }

    return (
        <>
            {pages.map((page, index) => {
                if (page.isSpecialPage) {
                    return (
                        <PartCFinalPage
                            key={`page-${index}`}
                            domain={page.domain}
                            pageNumber={page.pageNumber}
                        />
                    );
                } else {
                    return (
                        <PartCPage
                            key={`page-${index}`}
                            domains={page.domains}
                            isFirstPage={page.isFirstPage}
                            pageNumber={page.pageNumber}
                        />
                    );
                }
            })}
        </>
    );
};

export default PartC;